import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { BrandAppearanceModel } from '../models/brand-appearance.model';
import { BrandModel } from '../models/brand.model';

const DEFAULTS = {
  background: '#011a28',
  surface: '#062c3e',
  elevatedSurface: '#0a3e55',
  primary: '#ffffff',
  primaryContrast: '#011a28',
  secondary: '#06659b',
  secondaryContrast: '#ffffff',
  text: '#ffffff',
  mutedText: '#b9c8d0',
  border: '#ffffff',
  bodyFont: 'Metropolis',
  controlRadius: 6,
  cardRadius: 12,
  imageRadius: 6,
  borderWidth: 1
} as const;

const GOOGLE_STYLESHEET = /^https:\/\/fonts\.googleapis\.com\/css2\?family=[^\s]+&display=swap$/;

@Injectable({ providedIn: 'root' })
export class ThemeService {
  constructor(@Inject(DOCUMENT) private document: Document) { }

  apply(brand: BrandModel) {
    const appearance = brand?.appearance;
    if (!appearance)
      return;

    const root = this.document.documentElement;
    const backgroundColor = this.color(appearance.backgroundColor, DEFAULTS.background);
    const background = appearance.backgroundMode === 'gradient'
      ? `linear-gradient(${this.gradientDirection(appearance.backgroundGradientDirection)}, ${backgroundColor}, ${this.color(appearance.backgroundGradientColor, backgroundColor)})`
      : backgroundColor;
    const primary = this.color(appearance.primaryColor, DEFAULTS.primary);
    const secondary = this.color(appearance.secondaryColor, DEFAULTS.secondary);
    const text = this.color(appearance.textColor, DEFAULTS.text);

    const set = (name: string, value: string | number) => root.style.setProperty(name, String(value));
    set('--brand-background', background);
    set('--brand-background-color', backgroundColor);
    set('--brand-surface', this.color(appearance.surfaceColor, DEFAULTS.surface));
    set('--brand-elevated-surface', this.color(appearance.elevatedSurfaceColor, DEFAULTS.elevatedSurface));
    set('--brand-primary', primary);
    set('--brand-primary-contrast', this.color(appearance.primaryContrastColor, DEFAULTS.primaryContrast));
    set('--brand-secondary', secondary);
    set('--brand-secondary-contrast', this.color(appearance.secondaryContrastColor, DEFAULTS.secondaryContrast));
    set('--brand-text', text);
    set('--brand-muted-text', this.color(appearance.mutedTextColor, DEFAULTS.mutedText));
    const border = this.color(appearance.borderColor, DEFAULTS.border);
    set('--brand-border', border);
    set('--brand-divider', `rgb(${this.rgb(border)} / 32%)`);
    set('--brand-overlay', 'rgb(0 0 0 / 50%)');
    set('--brand-control-radius', `${this.number(appearance.controlRadius, DEFAULTS.controlRadius, 0, 32)}px`);
    set('--brand-card-radius', `${this.number(appearance.cardRadius, DEFAULTS.cardRadius, 0, 48)}px`);
    set('--brand-image-radius', `${this.number(appearance.imageRadius, DEFAULTS.imageRadius, 0, 48)}px`);
    set('--brand-border-width', `${this.number(appearance.borderWidth, DEFAULTS.borderWidth, 0, 8)}px`);
    set('--brand-shadow-style', this.shadow(appearance.shadowStyle));
    const tabBarBackground = this.color(appearance.tabBarBackgroundColor, this.color(appearance.elevatedSurfaceColor, DEFAULTS.elevatedSurface));
    set('--brand-tab-bar-background', tabBarBackground);
    set('--brand-tab-bar-background-rgb', this.rgb(tabBarBackground));
    set('--brand-tab-bar-active', this.color(appearance.tabBarActiveColor, text));
    set('--brand-tab-bar-inactive', this.color(appearance.tabBarInactiveColor, this.color(appearance.mutedTextColor, DEFAULTS.mutedText)));
    set('--brand-tab-bar-indicator', this.color(appearance.tabBarActiveColor, primary));
    set('--brand-tab-bar-radius', `${this.number(appearance.tabBarRadius, 12, 0, 64)}px`);
    set('--brand-tab-bar-shadow', this.shadow(appearance.tabBarShadowStyle));

    const bodyFont = this.fontStack(appearance.bodyFontFamily, DEFAULTS.bodyFont);
    const headingFont = this.fontStack(appearance.headingFontFamily, appearance.bodyFontFamily || DEFAULTS.bodyFont);
    set('--brand-font-body', bodyFont);
    set('--brand-font-heading', headingFont);
    set('--ion-font-family', bodyFont);
    set('--app-bg', background);
    set('--background', background);
    set('--ion-item-background', backgroundColor);
    set('--ion-background-color', backgroundColor);
    set('--ion-text-color', text);
    set('--color', text);

    this.applyIonicColor(root, 'primary', primary, this.color(appearance.primaryContrastColor, DEFAULTS.primaryContrast));
    this.applyIonicColor(root, 'secondary', secondary, this.color(appearance.secondaryContrastColor, DEFAULTS.secondaryContrast));
    this.applyIonicColor(root, 'success', this.optionalColor(appearance, 'successColor', '#2dd36f'), '#071a0e');
    this.applyIonicColor(root, 'warning', this.optionalColor(appearance, 'warningColor', '#ffc409'), '#241b00');
    this.applyIonicColor(root, 'danger', this.optionalColor(appearance, 'dangerColor', '#eb445a'), '#ffffff');
    this.applyIonicColor(root, 'light', '#f4f5f8', '#000000');
    this.applyIonicColor(root, 'medium', '#92949c', '#000000');
    this.applyIonicColor(root, 'dark', '#222428', '#ffffff');

    const themeColor = this.document.head.querySelector('meta[name="theme-color"]')
      || this.createThemeColorMeta();
    themeColor.setAttribute('content', primary);

    this.ensurePreconnect('google-fonts-preconnect', 'https://fonts.googleapis.com');
    this.ensurePreconnect('google-fonts-preconnect-static', 'https://fonts.gstatic.com');
    this.ensureStylesheet('google-font-body', appearance.bodyFontStylesheetUrl);
    this.ensureStylesheet('google-font-heading', appearance.headingFontStylesheetUrl);
  }

  private applyIonicColor(root: HTMLElement, name: string, color: string, contrast: string) {
    root.style.setProperty(`--ion-color-${name}`, color);
    root.style.setProperty(`--ion-color-${name}-contrast`, contrast);
    root.style.setProperty(`--ion-color-${name}-rgb`, this.rgb(color));
    root.style.setProperty(`--ion-color-${name}-shade`, this.adjust(color, -0.12));
    root.style.setProperty(`--ion-color-${name}-tint`, this.adjust(color, 0.12));
  }

  private createThemeColorMeta() {
    const meta = this.document.createElement('meta');
    meta.setAttribute('name', 'theme-color');
    this.document.head.appendChild(meta);
    return meta;
  }

  private fontStack(primary: string | undefined, fallback: string) {
    const families = [primary, fallback, 'Metropolis', 'Arial']
      .filter((x, index, all) => !!x && all.indexOf(x) === index && this.fontFamily(x))
      .map(x => `'${x!.replace(/'/g, '')}'`);
    return `${families.join(', ')}, sans-serif`;
  }

  private fontFamily(value: string) {
    return /^[\p{L}\p{N} .&-]{1,255}$/u.test(value);
  }

  private gradientDirection(value?: string) {
    return ['to bottom', 'to bottom right', 'to right', 'to top right'].includes(value || '')
      ? value!
      : 'to bottom right';
  }

  private color(value: string | undefined, fallback: string) {
    return /^#[0-9a-f]{6}$/i.test(value || '') ? value! : fallback;
  }

  private optionalColor(appearance: BrandAppearanceModel, property: string, fallback: string) {
    return this.color((appearance as unknown as Record<string, string | undefined>)[property], fallback);
  }

  private number(value: number | undefined, fallback: number, min: number, max: number) {
    return Number.isFinite(value) ? Math.min(max, Math.max(min, value!)) : fallback;
  }

  private shadow(value?: string) {
    switch (value) {
      case 'none':
        return 'none';
      case 'medium':
        return '0 1rem 3rem rgb(0 0 0 / 30%)';
      case 'strong':
        return '0 4px 16px rgb(0 0 0 / 35%)';
      default:
        return '0 0.25rem 0.75rem rgb(0 0 0 / 20%)';
    }
  }

  private rgb(value: string) {
    return `${parseInt(value.slice(1, 3), 16)}, ${parseInt(value.slice(3, 5), 16)}, ${parseInt(value.slice(5, 7), 16)}`;
  }

  private adjust(value: string, amount: number) {
    const channels = [1, 3, 5].map(index => parseInt(value.slice(index, index + 2), 16));
    const adjusted = channels.map(channel => Math.round(channel + (amount > 0 ? (255 - channel) : channel) * amount));
    return `#${adjusted.map(channel => channel.toString(16).padStart(2, '0')).join('')}`;
  }

  private ensurePreconnect(id: string, href: string) {
    if (this.document.getElementById(id))
      return;
    const link = this.document.createElement('link');
    link.id = id;
    link.rel = 'preconnect';
    link.href = href;
    if (href.includes('gstatic'))
      link.crossOrigin = 'anonymous';
    this.document.head.appendChild(link);
  }

  private ensureStylesheet(id: string, href?: string) {
    const existing = this.document.getElementById(id) as HTMLLinkElement | null;
    if (!href || !GOOGLE_STYLESHEET.test(href)) {
      existing?.remove();
      return;
    }
    if (existing?.getAttribute('href') === href)
      return;
    existing?.remove();
    const link = this.document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = href;
    this.document.head.appendChild(link);
  }
}
